import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { postreceiverQueryOptions } from '@/api/postreceiverOptions'
import { ApiErrorComponent } from '@/components/ApiErrorComponent'
import useGridPageSize from '@/components/hooks/useGridPageSize'
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel'
import useResponsive from '@/components/hooks/useResponsive'
import LoadProgress from '@/components/LoadProgress'
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar'
import { Box } from '@mui/material'
import {
    GridColDef,
    GridColumnVisibilityModel
} from '@mui/x-data-grid'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import { t } from 'i18next'
import React from 'react'

export const Route = createFileRoute('/_auth/postreceiver')({
    component: PostreceiverComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function PostreceiverComponent() {
    const postreceiverQuery = useSuspenseQuery(
        postreceiverQueryOptions(),
    )
    const postreceiver = postreceiverQuery.data

    const { xsmall, small, medium, large, xlarge } = useResponsive()

    const columns: GridColDef[] = []

    columns.push(
        {
            field: 'orgAbbreviation',
            headerName: 'Abkürzung',
        },
        {
            field: 'orgName',
            headerName: 'Organisation',
        },
        {
            field: 'divName',
            headerName: 'Landesverband',
        },
        {
            field: 'firstname',
            headerName: 'Vorname',
        },
        {
            field: 'lastname',
            headerName: 'Nachname',
        },
        {
            field: 'emailPrimary',
            headerName: t('property.member.emailPrimary'),
        },
        {
            field: 'job',
            headerName: t('entity.job'),
        },
        {
            field: 'street',
            headerName: t('property.member.street'),
        },
        {
            field: 'postcode',
            headerName: t('property.member.postcode'),
        },
        {
            field: 'city',
            headerName: t('property.member.city'),
        },

    )

    const initialVisibilityModel = {
        member: xsmall || small || medium,
        firstname: large || xlarge,
        lastname: large || xlarge,
        scoutId: large || xlarge,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('postreceiver', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('postreceiver', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('postreceiver', 35));

    const refreshData = () => {
        postreceiverQuery.refetch()
    }

    if (
        postreceiverQuery.isLoading ||
        postreceiverQuery.isFetching ||
        !postreceiver
    ) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <DataGridPremium
                columns={columns}
                rows={postreceiver}
                getRowId={(row) => {
                    return row.uuid || ''
                }}
                pagination
                pageSizeOptions={pageSizeOptions}
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => {
                    setPaginationModel(model)
                    gridPageSize.handlePaginationModelChange('postreceiver', model)
                }}
                columnVisibilityModel={visibilityModel}
                onColumnVisibilityModelChange={handleVisibilityModelChange}
                slots={{ toolbar: (props) => { return <OverviewGridToolbar {...props} scope="postreceiver" refresh={refreshData} /> } }}
                slotProps={{
                    toolbar: {
                        csvOptions: {
                            disableToolbarButton: true
                        },
                        printOptions: {
                            disableToolbarButton: true
                        }
                    }
                }}
                density='compact'
                hideFooter={true}
                disableDensitySelector
                disableAggregation
                disableColumnPinning
                disableColumnReorder
                disableRowGrouping
                disableRowSelectionOnClick
                disablePivoting
                showToolbar />
        </Box>
    );
}
